import React, { useState, useMemo } from "react";
import { Box, Typography, Button, ButtonGroup, IconButton, Chip, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem } from "@mui/material";
import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Close,
  FilterList,
} from "@mui/icons-material";
import { samplePMCals } from "./pmCalData";

import ResponsiveDialog from "../../Component/ResponsiveDialog";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
const toKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const toKeyFromDate = (date) =>
  toKey(date.getFullYear(), date.getMonth(), date.getDate());
const formatHour = (h) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hr}:00 ${ampm}`;
};

const getEventColor = (pmCal) => {
  const dueDate = new Date(pmCal.dueDate);
  const today = new Date();
  switch (pmCal.status) {
    case "completed":
      return "#10b981";
    case "in-progress":
      return "#f59e0b";
    case "pending":
      return dueDate < today ? "#ef4444" : "#3b82f6";
    default:
      return "#6b7280";
  }
};

const getEventTextColor = (pmCal) => {
  return pmCal.status === "in-progress" ? "#000" : "#fff";
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "in-progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const HOURS = Array.from({ length: 10 }, (_, i) => i + 8); // 8am to 5pm

const buildEvents = (pmCals) =>
  pmCals.map((p) => ({
    ...p,
    dateKey: p.startDate
      ? (() => {
          const d = new Date(p.startDate);
          return isNaN(d.getTime())
            ? null
            : toKey(d.getFullYear(), d.getMonth(), d.getDate());
        })()
      : null,
    color: getEventColor(p),
    textColor: getEventTextColor(p),
  }));

// ─── Component ────────────────────────────────────────────────────────────────
const PMCalPlanner = () => {
  const today = new Date();
  const [viewDate, setViewDate] = useState({ year: 2026, month: 3 });
  const [calView, setCalView] = useState("month");
  const [statusFilter, setStatusFilter] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [localPMCals, setLocalPMCals] = useState(samplePMCals);
  const [selectedDay, setSelectedDay] = useState(new Date(2026, 3, 1));

  const { year, month } = viewDate;

  // Filter events by status
  const filteredPMCals = useMemo(() => {
    return statusFilter
      ? localPMCals.filter((p) => p.status === statusFilter)
      : localPMCals;
  }, [statusFilter, localPMCals]);

  const events = useMemo(() => buildEvents(filteredPMCals), [filteredPMCals]);

  // Map events by date key
  const eventMap = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      if (ev.dateKey) {
        if (!map[ev.dateKey]) map[ev.dateKey] = [];
        map[ev.dateKey].push(ev);
      }
    });
    return map;
  }, [events]);

  // ── Month grid setup ──
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInPrev = getDaysInMonth(year, month === 0 ? 11 : month - 1);

  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: daysInPrev - firstDay + 1 + i, curMonth: false, month: month - 1, year });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, curMonth: true, month, year });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, curMonth: false, month: month + 1, year });
  }

  // ── Week helpers ──
  const getWeekStart = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    return d;
  };

  const weekStart = useMemo(() => getWeekStart(selectedDay), [selectedDay]);
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  // ── Navigation ──
  const goBack = () => {
    if (calView === "month") {
      setViewDate(({ year, month }) =>
        month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 }
      );
    } else if (calView === "week") {
      setSelectedDay((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() - 7);
        return d;
      });
    } else {
      setSelectedDay((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() - 1);
        return d;
      });
    }
  };
  const goForward = () => {
    if (calView === "month") {
      setViewDate(({ year, month }) =>
        month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 }
      );
    } else if (calView === "week") {
      setSelectedDay((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() + 7);
        return d;
      });
    } else {
      setSelectedDay((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() + 1);
        return d;
      });
    }
  };
  const goToday = () => {
    setViewDate({ year: today.getFullYear(), month: today.getMonth() });
    setSelectedDay(new Date(today));
  };

  const isToday = (cell) =>
    cell.curMonth &&
    cell.day === today.getDate() &&
    cell.month === today.getMonth() &&
    cell.year === today.getFullYear();

  const isTodayDate = (date) =>
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const isWeekend = (colIndex) => colIndex % 7 === 0 || colIndex % 7 === 6;

  const handleEventClick = (ev) => {
    setSelectedEvent(ev);
    setDetailOpen(true);
  };

  const handleStatusUpdate = (newStatus) => {
    setLocalPMCals((prev) =>
      prev.map((p) =>
        p.id === selectedEvent.id ? { ...p, status: newStatus } : p
      )
    );
    setDetailOpen(false);
  };

  const handleEdit = () => {
    setDetailOpen(false);
    alert(`Edit PM-Cal: ${selectedEvent.id} (placeholder)`);
  };

  const statusLabel = (s) => {
    const map = { pending: "Pending", "in-progress": "In Progress", completed: "Completed" };
    return map[s] || s;
  };

  const statusChipStyle = (s) => {
    const map = {
      pending: { bgcolor: "#dbeafe", color: "#1e40af" },
      "in-progress": { bgcolor: "#fef3c7", color: "#92400e" },
      completed: { bgcolor: "#dcfce7", color: "#15803d" },
    };
    return map[s] || { bgcolor: "#f1f5f9", color: "#475569" };
  };

  // ── Title text for nav row ──
  const getNavTitle = () => {
    if (calView === "month") return `${MONTH_NAMES[month]} ${year}`;
    if (calView === "week") {
      const ws = weekDays[0];
      const we = weekDays[6];
      return `${MONTH_NAMES[ws.getMonth()]} ${ws.getDate()} – ${
        ws.getMonth() !== we.getMonth() ? MONTH_NAMES[we.getMonth()] + " " : ""
      }${we.getDate()}, ${we.getFullYear()}`;
    }
    return `${MONTH_NAMES[selectedDay.getMonth()]} ${selectedDay.getDate()}, ${selectedDay.getFullYear()}`;
  };

  // ── Shared event pill renderer ──
  const renderEventPill = (ev) => (
    <Box
      key={ev.id}
      onClick={() => handleEventClick(ev)}
      sx={{
        bgcolor: ev.color, color: ev.textColor,
        borderRadius: "4px", px: 0.8, py: "3px",
        fontSize: 11, fontWeight: 500, mb: 0.4,
        whiteSpace: "nowrap", overflow: "hidden",
        textOverflow: "ellipsis", cursor: "pointer",
        fontFamily: "monospace",
        "&:hover": { opacity: 0.85 },
      }}
      title={`${ev.id} - ${ev.assetName} (${statusLabel(ev.status)})`}
    >
      {ev.id} - {ev.assetName}
    </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ── Page Header ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e0e4ea", px: 3.5, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 36, height: 36, bgcolor: "#e8f0fe", borderRadius: 2,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <CalendarMonth sx={{ color: "#2362B4", fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#2362B4", letterSpacing: "-0.3px" }}>
            PM-Cal Planner
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.3, ml: "46px" }}>
          Calendar view of PM-Cal activities
        </Typography>
      </Box>

      {/* ── Calendar Card ── */}
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>

          {/* Toolbar */}
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2.5, py: 2, borderBottom: "1px solid #e0e4ea",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 16, color: "#2362B4" }}>
              Calendar View
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FilterList sx={{ fontSize: 18, color: "#6b7280" }} />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={{ fontSize: 13, borderRadius: 1.5 }}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiButton-root": {
                    borderColor: "#2362B4", color: "#2362B4",
                    textTransform: "none", fontWeight: 500,
                  },
                }}
              >
                {["Month", "Week", "Day"].map((v) => (
                  <Button
                    key={v}
                    onClick={() => {
                      setCalView(v.toLowerCase());
                      if (v.toLowerCase() !== "month") {
                        setSelectedDay(new Date(year, month, 1));
                      }
                    }}
                    sx={
                      calView === v.toLowerCase()
                        ? { bgcolor: "#2362B4 !important", color: "#fff !important" }
                        : {}
                    }
                  >
                    {v}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>

          {/* Nav Row */}
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2.5, pt: 1.8, pb: 1.2,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={goBack}
                sx={{
                  bgcolor: "#2362B4", color: "#fff", borderRadius: 1.5,
                  width: 30, height: 30, "&:hover": { bgcolor: "#1B4F96" },
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={goForward}
                sx={{
                  bgcolor: "#2362B4", color: "#fff", borderRadius: 1.5,
                  width: 30, height: 30, "&:hover": { bgcolor: "#1B4F96" },
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
              <Button
                size="small"
                onClick={goToday}
                sx={{
                  bgcolor: "#9e9e9e", color: "#fff", textTransform: "none",
                  borderRadius: 1.5, minWidth: 60, fontSize: 13,
                  "&:hover": { bgcolor: "#757575" },
                }}
              >
                today
              </Button>
            </Box>

            <Typography sx={{ fontWeight: 700, fontSize: 22, color: "#1a2333" }}>
              {getNavTitle()}
            </Typography>

            {/* Legend */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              {[
                { label: "Pending", color: "#3b82f6" },
                { label: "In Progress", color: "#f59e0b" },
                { label: "Completed", color: "#10b981" },
                { label: "Overdue", color: "#ef4444" },
              ].map((l) => (
                <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: l.color, borderRadius: "2px" }} />
                  <Typography sx={{ fontSize: 11, color: "#6b7280" }}>{l.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ═══════ MONTH VIEW ═══════ */}
          {calView === "month" && (
            <>
              {/* Day Headers */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderTop: "1px solid #e0e4ea" }}>
                {DAYS.map((d) => (
                  <Box
                    key={d}
                    sx={{
                      py: 1, textAlign: "center", fontSize: 13, fontWeight: 600,
                      color: "#2362B4", borderRight: "1px solid #e0e4ea",
                      "&:last-child": { borderRight: "none" },
                    }}
                  >
                    {d}
                  </Box>
                ))}
              </Box>

              {/* Calendar Cells */}
              <Box>
                {Array.from({ length: 6 }).map((_, weekIdx) => (
                  <Box
                    key={weekIdx}
                    sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderTop: "1px solid #e0e4ea" }}
                  >
                    {cells.slice(weekIdx * 7, weekIdx * 7 + 7).map((cell, colIdx) => {
                      const key = toKey(cell.year, cell.month, cell.day);
                      const cellEvents = eventMap[key] || [];
                      const weekend = isWeekend(colIdx);
                      const todayCell = isToday(cell);

                      return (
                        <Box
                          key={colIdx}
                          sx={{
                            minHeight: 110, p: "6px 8px",
                            borderRight: colIdx < 6 ? "1px solid #e0e4ea" : "none",
                            bgcolor: todayCell
                              ? "#fffde7"
                              : !cell.curMonth
                              ? "#f8f9fb"
                              : weekend
                              ? "#f8f9fb"
                              : "#fff",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13, fontWeight: 500, textAlign: "right",
                              color: !cell.curMonth ? "#c4c9d4" : "#1a2333",
                              display: "block", mb: 0.5,
                            }}
                          >
                            {cell.day}
                          </Typography>
                          {cellEvents.map(renderEventPill)}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* ═══════ WEEK VIEW ═══════ */}
          {calView === "week" && (
            <>
              {/* Day column headers */}
              <Box sx={{ display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)", borderTop: "1px solid #e0e4ea" }}>
                <Box sx={{ borderRight: "1px solid #e0e4ea", bgcolor: "#f8f9fb" }} />
                {weekDays.map((wd, i) => (
                  <Box
                    key={i}
                    sx={{
                      py: 1, textAlign: "center", borderRight: i < 6 ? "1px solid #e0e4ea" : "none",
                      bgcolor: isTodayDate(wd) ? "#fffde7" : "#f8f9fb",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#2362B4" }}>
                      {DAYS[wd.getDay()]}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 18, fontWeight: 700,
                        color: isTodayDate(wd) ? "#2362B4" : "#1a2333",
                      }}
                    >
                      {wd.getDate()}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Time grid rows */}
              {HOURS.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)",
                    borderTop: "1px solid #e0e4ea", minHeight: 60,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex", alignItems: "flex-start", justifyContent: "center",
                      pt: 0.5, borderRight: "1px solid #e0e4ea", bgcolor: "#f8f9fb",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>
                      {formatHour(hour)}
                    </Typography>
                  </Box>
                  {weekDays.map((wd, colIdx) => {
                    const key = toKeyFromDate(wd);
                    const dayEvents = eventMap[key] || [];
                    // Show all events for the day in the 8am row only (since sample data has dates but no times)
                    const showEvents = hour === 8 ? dayEvents : [];
                    return (
                      <Box
                        key={colIdx}
                        sx={{
                          p: "4px 6px",
                          borderRight: colIdx < 6 ? "1px solid #e0e4ea" : "none",
                          bgcolor: isTodayDate(wd) ? "#fffde7" : "#fff",
                        }}
                      >
                        {showEvents.map(renderEventPill)}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </>
          )}

          {/* ═══════ DAY VIEW ═══════ */}
          {calView === "day" && (
            <>
              {/* Day header */}
              <Box
                sx={{
                  display: "grid", gridTemplateColumns: "60px 1fr",
                  borderTop: "1px solid #e0e4ea",
                }}
              >
                <Box sx={{ borderRight: "1px solid #e0e4ea", bgcolor: "#f8f9fb" }} />
                <Box
                  sx={{
                    py: 1.5, textAlign: "center",
                    bgcolor: isTodayDate(selectedDay) ? "#fffde7" : "#f8f9fb",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#2362B4" }}>
                    {DAYS[selectedDay.getDay()]}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 22, fontWeight: 700,
                      color: isTodayDate(selectedDay) ? "#2362B4" : "#1a2333",
                    }}
                  >
                    {selectedDay.getDate()}
                  </Typography>
                </Box>
              </Box>

              {/* Time grid rows */}
              {HOURS.map((hour) => {
                const key = toKeyFromDate(selectedDay);
                const dayEvents = eventMap[key] || [];
                const showEvents = hour === 8 ? dayEvents : [];
                return (
                  <Box
                    key={hour}
                    sx={{
                      display: "grid", gridTemplateColumns: "60px 1fr",
                      borderTop: "1px solid #e0e4ea", minHeight: 60,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex", alignItems: "flex-start", justifyContent: "center",
                        pt: 0.5, borderRight: "1px solid #e0e4ea", bgcolor: "#f8f9fb",
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>
                        {formatHour(hour)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: "4px 8px",
                        bgcolor: isTodayDate(selectedDay) ? "#fffde7" : "#fff",
                      }}
                    >
                      {showEvents.map(renderEventPill)}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>

      {/* ── Event Detail Dialog ── */}
      <ResponsiveDialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "#2362B4", color: "#fff",
            display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>PM-Cal Details</Typography>
          <IconButton onClick={() => setDetailOpen(false)} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedEvent && (
            <Box>
              <Box sx={{ display: "flex", gap: 4, mb: 2, mt: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1a2333", mb: 1.5 }}>
                    PM-Cal Information
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>ID:</strong> {selectedEvent.id}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Status:</strong>{" "}
                    <Chip
                      label={statusLabel(selectedEvent.status)}
                      size="small"
                      sx={{ ...statusChipStyle(selectedEvent.status), fontWeight: 600, fontSize: 11, height: 22 }}
                    />
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Asset:</strong> {selectedEvent.assetName}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Tool:</strong> {selectedEvent.toolName || "N/A"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1a2333", mb: 1.5 }}>
                    Schedule
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Start Date:</strong> {selectedEvent.startDate}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Due Date:</strong> {selectedEvent.dueDate}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Frequency:</strong> {selectedEvent.frequency || "N/A"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                    <strong>Priority:</strong> {selectedEvent.priority || "N/A"}
                  </Typography>
                </Box>
              </Box>
              {selectedEvent.description && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: 13 }}>
                    <strong>Description:</strong> {selectedEvent.description}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selectedEvent && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={handleEdit}
                sx={{
                  bgcolor: "#2362B4", textTransform: "none", fontWeight: 600, fontSize: 13,
                  "&:hover": { bgcolor: "#1B4F96" },
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleStatusUpdate("in-progress")}
                sx={{
                  bgcolor: "#10b981", textTransform: "none", fontWeight: 600, fontSize: 13,
                  "&:hover": { bgcolor: "#059669" },
                }}
              >
                Start
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleStatusUpdate("completed")}
                sx={{
                  bgcolor: "#f59e0b", color: "#000", textTransform: "none", fontWeight: 600, fontSize: 13,
                  "&:hover": { bgcolor: "#d97706" },
                }}
              >
                Complete
              </Button>
            </>
          )}
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default PMCalPlanner;
